// ===========================================
// BUSTED! BLACKJACK - WEB ANIMATION RENDERER
// ===========================================
// Platform-specific animation renderer for web (React)
// Implements AnimationRenderer interface from shared package
// ===========================================

import {
  AnimationRenderer,
  BlackjackEvent,
  getEventTiming as _getEventTiming,
  AnimationTimingConfig,
  DEFAULT_WEB_TIMING,
} from '@busted-blackjack/shared';

// ===========================================
// TYPES
// ===========================================

export interface AnimationCallbacks {
  // Betting animations
  onBetStaged?: (seatPosition: number, amount: number) => Promise<void>;
  onBetConfirmed?: (seatPosition: number, amount: number) => Promise<void>;
  onBetUnstaged?: (seatPosition: number) => Promise<void>;

  // Dealing animations
  onHandBoxCreated?: (seatPosition: number, handIndex: number, bet: number) => Promise<void>;
  onCardDealt?: (
    target: 'player' | 'dealer',
    seatPosition: number | undefined,
    handIndex: number,
    cardIndex: number
  ) => Promise<void>;
  onScoreUpdated?: (
    target: 'player' | 'dealer',
    seatPosition: number | undefined,
    score: number
  ) => Promise<void>;

  // Turn animations
  onTurnStarted?: (seatPosition: number, handIndex: number) => Promise<void>;
  onActionTaken?: (seatPosition: number, action: string) => Promise<void>;
  onTurnEnded?: (seatPosition: number) => Promise<void>;

  // Outcome animations
  onHandBusted?: (seatPosition: number, handIndex: number) => Promise<void>;
  onHandBlackjack?: (seatPosition: number, handIndex: number) => Promise<void>;
  onHandSplit?: (seatPosition: number) => Promise<void>;

  // Dealer animations
  onDealerRevealsHole?: () => Promise<void>;
  onDealerHits?: (cardIndex: number) => Promise<void>;
  onDealerStands?: () => Promise<void>;
  onDealerBusts?: () => Promise<void>;
  onDealerBlackjack?: () => Promise<void>;

  // Resolution animations
  onHandResolved?: (seatPosition: number, handIndex: number, outcome: string) => Promise<void>;
  onChipsPaid?: (seatPosition: number, handIndex: number, amount: number) => Promise<void>;
  onChipsCollected?: (seatPosition: number, handIndex: number, amount: number) => Promise<void>;

  // Round animations
  onRoundStarted?: (roundNumber: number, buttonPosition: number) => Promise<void>;
  onBettingOpened?: () => Promise<void>;
  onRoundCompleted?: () => Promise<void>;
}

export interface WebAnimationRendererOptions {
  callbacks?: AnimationCallbacks;
  timingConfig?: AnimationTimingConfig;
  enableSounds?: boolean;
  soundPlayer?: SoundPlayer;
}

export interface SoundPlayer {
  play: (soundName: string) => void;
}

// ===========================================
// WEB ANIMATION RENDERER
// ===========================================

export class WebAnimationRenderer implements AnimationRenderer {
  private callbacks: AnimationCallbacks;
  private _timingConfig: AnimationTimingConfig;
  private enableSounds: boolean;
  private soundPlayer?: SoundPlayer;

  constructor(options: WebAnimationRendererOptions = {}) {
    this.callbacks = options.callbacks ?? {};
    this._timingConfig = options.timingConfig ?? DEFAULT_WEB_TIMING;
    this.enableSounds = options.enableSounds ?? true;
    this.soundPlayer = options.soundPlayer;
  }

  // ----- AnimationRenderer Interface -----

  canRender(event: BlackjackEvent): boolean {
    // List of event types that have animations
    const animatedEvents = [
      'ROUND_STARTED',
      'BETTING_OPENED',
      'BET_STAGED',
      'BET_UNSTAGED',
      'BET_CONFIRMED',
      'HAND_BOX_CREATED',
      'CARD_DEALT',
      'SCORE_UPDATED',
      'TURN_STARTED',
      'ACTION_TAKEN',
      'TURN_ENDED',
      'HAND_SPLIT',
      'HAND_BUSTED',
      'HAND_BLACKJACK',
      'DEALER_REVEALS_HOLE',
      'DEALER_HITS',
      'DEALER_STANDS',
      'DEALER_BUSTS',
      'DEALER_BLACKJACK',
      'HAND_RESOLVED',
      'CHIPS_PAID',
      'CHIPS_COLLECTED',
      'ROUND_COMPLETED',
    ];

    return animatedEvents.includes(event.type);
  }

  async renderEvent(event: BlackjackEvent): Promise<void> {

    // Play sound if applicable
    this.playEventSound(event);

    // Dispatch to appropriate handler
    switch (event.type) {
      case 'ROUND_STARTED':
        await this.callbacks.onRoundStarted?.(event.roundNumber, event.buttonPosition);
        break;

      case 'BETTING_OPENED':
        await this.callbacks.onBettingOpened?.();
        break;

      case 'BET_STAGED':
        await this.callbacks.onBetStaged?.(event.seatPosition, event.amount);
        break;

      case 'BET_UNSTAGED':
        await this.callbacks.onBetUnstaged?.(event.seatPosition);
        break;

      case 'BET_CONFIRMED':
        await this.callbacks.onBetConfirmed?.(event.seatPosition, event.amount);
        break;

      case 'HAND_BOX_CREATED':
        await this.callbacks.onHandBoxCreated?.(event.seatPosition, event.handIndex, event.bet);
        break;

      case 'CARD_DEALT':
        await this.callbacks.onCardDealt?.(
          event.target,
          event.seatPosition,
          event.handIndex,
          event.cardIndex
        );
        break;

      case 'SCORE_UPDATED':
        await this.callbacks.onScoreUpdated?.(event.target, event.seatPosition, event.score);
        break;

      case 'TURN_STARTED':
        await this.callbacks.onTurnStarted?.(event.seatPosition, event.handIndex);
        break;

      case 'ACTION_TAKEN':
        await this.callbacks.onActionTaken?.(event.seatPosition, event.action);
        break;

      case 'TURN_ENDED':
        await this.callbacks.onTurnEnded?.(event.seatPosition);
        break;

      case 'HAND_SPLIT':
        await this.callbacks.onHandSplit?.(event.seatPosition);
        break;

      case 'HAND_BUSTED':
        await this.callbacks.onHandBusted?.(event.seatPosition, event.handIndex);
        break;

      case 'HAND_BLACKJACK':
        await this.callbacks.onHandBlackjack?.(event.seatPosition, event.handIndex);
        break;

      case 'DEALER_REVEALS_HOLE':
        await this.callbacks.onDealerRevealsHole?.();
        break;

      case 'DEALER_HITS':
        await this.callbacks.onDealerHits?.(event.card ? 1 : 0);
        break;

      case 'DEALER_STANDS':
        await this.callbacks.onDealerStands?.();
        break;

      case 'DEALER_BUSTS':
        await this.callbacks.onDealerBusts?.();
        break;

      case 'DEALER_BLACKJACK':
        await this.callbacks.onDealerBlackjack?.();
        break;

      case 'HAND_RESOLVED':
        console.log(`[TIMING] HAND_RESOLVED rendered at ${Date.now()} - outcome: ${event.outcome}, hand: ${event.handIndex}`);
        await this.callbacks.onHandResolved?.(
          event.seatPosition,
          event.handIndex,
          event.outcome
        );
        break;

      case 'CHIPS_PAID':
        console.log(`[TIMING] CHIPS_PAID render START at ${Date.now()} - hand: ${event.handIndex}, amount: ${event.amount}`);
        await this.callbacks.onChipsPaid?.(event.seatPosition, event.handIndex, event.amount);
        console.log(`[TIMING] CHIPS_PAID render END at ${Date.now()}`);
        break;

      case 'CHIPS_COLLECTED':
        await this.callbacks.onChipsCollected?.(event.seatPosition, event.handIndex, event.amount);
        break;

      case 'ROUND_COMPLETED':
        await this.callbacks.onRoundCompleted?.();
        break;
    }

    // NOTE: Minimum timing is applied by AnimationQueueProcessor.processNext(),
    // which already calls getEventTiming() and waits the remainder if the callback
    // finished faster than the minimum. Adding a delay here would double it.
  }

  onQueueCleared(): void {
    // Could reset any animation state here
  }

  onQueueEmpty(): void {
    // Could trigger "caught up" indicator
  }

  // ----- Sound Handling -----

  private playEventSound(event: BlackjackEvent): void {
    if (!this.enableSounds || !this.soundPlayer) return;

    const soundMap: Partial<Record<BlackjackEvent['type'], string>> = {
      BET_STAGED: 'chip_place',
      BET_CONFIRMED: 'chip_stack',
      CARD_DEALT: 'card_deal',
      HAND_BUSTED: 'bust',
      HAND_BLACKJACK: 'blackjack',
      DEALER_REVEALS_HOLE: 'card_flip',
      DEALER_BUSTS: 'dealer_bust',
      CHIPS_PAID: 'chips_win',
      CHIPS_COLLECTED: 'chips_lose',
    };

    const sound = soundMap[event.type];
    if (sound) {
      this.soundPlayer.play(sound);
    }
  }

  // ----- Utilities -----

  private _delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // ----- Configuration -----

  setCallbacks(callbacks: AnimationCallbacks): void {
    this.callbacks = callbacks;
  }

  updateCallback<K extends keyof AnimationCallbacks>(
    key: K,
    callback: AnimationCallbacks[K]
  ): void {
    this.callbacks[key] = callback;
  }

  setTimingConfig(config: AnimationTimingConfig): void {
    this._timingConfig = config;
  }

  setSoundPlayer(player: SoundPlayer): void {
    this.soundPlayer = player;
  }

  setEnableSounds(enabled: boolean): void {
    this.enableSounds = enabled;
  }
}

// ===========================================
// FACTORY
// ===========================================

export function createWebAnimationRenderer(
  options: WebAnimationRendererOptions = {}
): WebAnimationRenderer {
  return new WebAnimationRenderer(options);
}

export default WebAnimationRenderer;
